<!-- @component Stacked timeseries chart that displays a value broken up by category, with interactivity. -->

<script context="module">
  let counter = 0; // for element IDs
</script>

<script>
  import { scaleOrdinal, scaleTime, scaleUtc } from "d3-scale";
  import { flatten, LayerCake, stack, Svg } from "layercake";
  import { createEventDispatcher } from "svelte";

  import { dateInUserTimezone } from "$lib/format";
  import AxisX from "./_components/AxisX.svelte";
  import AxisY from "./_components/AxisY.svelte";
  import Brush from "./_components/Brush.svelte";
  import MouseTracker from "./_components/MouseTracker.svelte";
  import StackedArea from "./_components/StackedArea.svelte";
  import StackedBar from "./_components/StackedBar.svelte";
  import StackedTooltip from "./_components/StackedTooltip.svelte";
  import { getHoverIndex, getTimeFormat } from "./_components/chartUtils";

  /** @type {String} The title of the chart. */
  export let title;

  /** @type {[number, number|null]} The y-axis domain. */
  export let yDomain = [0, null];

  /** @type {number} Pixel padding value for the data extent at the top of the chart. */
  export let yPaddingTop = 0;

  /** @type {[Date, Date]} Time interval to display in the x-axis. */
  export let xDomain;

  /** @type {function(number): any} How to format the y-axis ticks. */
  export let formatLabelY = (value) => value;

  /** @type {function(any, group): string} How to format values in the chart tooltip. */
  export let formatTooltip;

  /** @type {boolean} Displays a tooltip when the user hovers over the chart. Defaults to true. */
  export let tooltip = true;

  /** @type {boolean} Displays the x-axis (time) in UTC, rather than the local timezone. */
  export let displayUtc = false;

  /** @type {string[]} Names for each bar type in the stack */
  export let seriesNames;

  /** @type {string[]} Colors for each bar type in the stack */
  export let seriesColors;

  /** Turn on interactivity, sending events to the parent. */
  export let interactive = true;

  /** @type {Date | undefined} The currently hovered date, use with `bind:` to synchronize. */
  export let hoveringX = undefined;

  /** @type {number | undefined} The currently hovered index, use with `bind:` to synchronize. */
  export let hoveredIndex = undefined;

  /** @type {number | undefined} Highlight a specific data region. */
  export let selectIndex = undefined;

  /** @type {"Area" | "Bar"} Variant of chart. Currently supports a stacked bar chart and stacked area chart.*/
  export let variant;

  /** @type {number} Number of ticks along the y-axis. */
  export let yTicks = 4;

  /** @type {boolean} Displays the x-axis and y-axis. Defaults to true. */
  export let axis = true;

  /** @type {{top: number, right: number, bottom: number, left: number }} Padding of the chart within its container. */
  export let padding = { top: 8, right: 16, bottom: 24, left: 36 };

  /**
   * @type {Array} Data points to display in the chart.
   *
   * Each data point should be an object in the following format:
   * `{ date: Date, key1: number, key2: number, ... }`.
   */
  export let data;

  const dispatch = createEventDispatcher();

  const xKey = "date";
  const yKey = [0, 1];
  const zKey = "key";

  $: stackedData = stack(data, seriesNames);
  $: xScale = displayUtc ? scaleUtc() : scaleTime();
  $: duration = xDomain ? xDomain[1].getTime() - xDomain[0].getTime() : 0;
  $: formatTimeTooltip = getTimeFormat(displayUtc, duration);
  $: xTickFormatAdjusted = (date) => {
    return xScale.tickFormat()(dateInUserTimezone(date));
  };
  $: hasNonzeroData = data.some((d) => seriesNames.some((key) => d[key] > 0));

  const nonzero = (f) => {
    return (n) => (n ? f(n) : "");
  };

  const idSuffix = "StackedTimeseriesChart-" + counter++;

  $: hoveredIndex = getHoverIndex(
    data,
    hoveringX,
    (d) => d.date,
    variant === "Bar" ? "down" : "closest",
  );
</script>

<div class="w-full h-full flex flex-col">
  {#if title}
    <h3
      class="flex-shrink-0 text-center text-sm text-zinc-200 font-medium mb-1"
    >
      {title}
    </h3>
  {/if}
  <div class="flex-1">
    <LayerCake
      {padding}
      x={(d) => d.data[xKey]}
      y={yKey}
      z={zKey}
      {xScale}
      {xDomain}
      yDomain={hasNonzeroData ? yDomain : [0, 2]}
      yPadding={[0, yPaddingTop]}
      zDomain={seriesNames}
      zScale={scaleOrdinal()}
      zRange={seriesColors}
      data={stackedData}
      flatData={flatten(stackedData)}
      let:width
      let:height
    >
      <Svg>
        <defs>
          <clipPath id="clip-{idSuffix}">
            <!-- Prevent the line from rendering outside the main chart area. -->
            <rect x={-1} y={-1} width={width + 2} height={height + 2} />
          </clipPath>
        </defs>

        {#if axis}
          <AxisX
            ticks={Math.floor(width / 80)}
            format={xTickFormatAdjusted}
            tickMarks
            gridlines={false}
          />
          <AxisY ticks={yTicks} format={nonzero(formatLabelY)} />
        {/if}

        <MouseTracker bind:x={hoveringX}>
          <g
            clip-path="url(#clip-{idSuffix})"
            class:cursor-crosshair={interactive}
          >
            {#if variant === "Area"}
              <StackedArea {selectIndex} {hoveringX} />
            {:else if variant === "Bar"}
              <StackedBar
                showEmptyBars={!interactive}
                hideHoverState={!interactive}
                {hoveringX}
                {selectIndex}
              />
            {/if}
          </g>

          {#if interactive || tooltip}
            {#if tooltip}
              <StackedTooltip
                formatX={(d) => formatTimeTooltip(d)}
                formatY={formatTooltip}
                {hoveredIndex}
                {seriesNames}
              />
            {/if}

            <Brush
              enableBrushZoom={interactive}
              on:changeDomainX={({ detail }) => {
                hoveringX = undefined;
                dispatch("changeDomainX", detail);
              }}
            />
          {/if}
        </MouseTracker>
      </Svg>
    </LayerCake>
  </div>
</div>
